import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from 'recharts';
import { Loader2, AlertTriangle, TrendingUp, Building2, MapPin, Euro, Activity, Flame } from 'lucide-react';
import { groupByProvince } from '../utils/portfolioSort';
import { ColumnPicker, useVisibleColumns } from '../components/app/ColumnPicker';

const eur = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmt = (n) => new Intl.NumberFormat('es-ES').format(n || 0);

const TYPE_COLORS = {
  ruidos: '#f87171',
  impermeabilizacion: '#60a5fa',
  estructural: '#a78bfa',
  suciedad: '#fbbf24',
  zonas_comunes: '#34d399',
  otro: '#94a3b8',
  humedad: '#22d3ee',
  vegetacion: '#84cc16',
  grietas: '#e879f9',
};

const Kpi = ({ icon: Icon, label, value, sub, tone = 'blue' }) => {
  const tones = {
    blue: 'text-blue-300 border-blue-500/30 bg-blue-500/5',
    red: 'text-red-400 border-red-500/30 bg-red-500/5',
    orange: 'text-orange-400 border-orange-500/30 bg-orange-500/5',
    green: 'text-[#A3FF12] border-[#A3FF12]/30 bg-[#A3FF12]/5',
  };
  return (
    <div className={`border rounded-2xl p-4 ${tones[tone]}`} data-testid={`totem-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide opacity-80"><Icon size={14}/> {label}</div>
      <div className="text-3xl font-black mt-2">{value}</div>
      {sub && <div className="text-white/50 text-xs mt-1">{sub}</div>}
    </div>
  );
};

const PROBLEMATIC_COLUMNS = [
  { key: 'site', label: 'Site', defaultVisible: true, always: true,
    render: (s) => (
      <td className="px-2 py-2 font-mono text-blue-200" key="s">
        {s.site_code}
        {s.site?.code && <div className="text-white/40 text-[10px]">Portfolio: {s.site.code}</div>}
      </td>
    ),
  },
  { key: 'address', label: 'Dirección', defaultVisible: true,
    render: (s) => (
      <td className="px-2 py-2 text-white/70" key="a">
        {s.site ? (
          <>
            <div>{s.site.address || '—'}</div>
            <div className="text-white/40 text-[10px]">{s.site.municipality} · {s.site.province}</div>
          </>
        ) : <span className="text-orange-300/60">Sin site en portfolio</span>}
      </td>
    ),
  },
  { key: 'count', label: 'Incidencias', defaultVisible: true, align: 'right',
    render: (s) => <td className="px-2 py-2 text-right font-black text-red-300" key="c">{s.count}</td>,
  },
  { key: 'cost', label: 'Coste TOTEM', defaultVisible: true, align: 'right',
    render: (s, ctx) => <td className="px-2 py-2 text-right text-red-200" key="cost">{ctx.eur(s.cost)}</td>,
  },
  { key: 'types', label: 'Tipos', defaultVisible: true,
    render: (s, ctx) => (
      <td className="px-2 py-2" key="t">
        <div className="flex flex-wrap gap-1">
          {(s.types || []).map((t) => (
            <span key={t} className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: (ctx.TYPE_COLORS[t] || '#666') + '30', color: ctx.TYPE_COLORS[t] || '#ccc' }}>{t}</span>
          ))}
        </div>
      </td>
    ),
  },
  { key: 'last', label: 'Última', defaultVisible: true,
    render: (s) => <td className="px-2 py-2 text-white/50 text-[10px]" key="l">{s.last_incident_at?.slice(0, 10) || '—'}</td>,
  },
  { key: 'municipality', label: 'Municipio', defaultVisible: false,
    render: (s) => <td className="px-2 py-2 text-white/60 text-[11px]" key="mu">{s.site?.municipality || '—'}</td>,
  },
  { key: 'owner', label: 'Propietario', defaultVisible: false,
    render: (s) => <td className="px-2 py-2 text-white/60 text-[11px]" key="ow">{s.site?.owner_name || '—'}</td>,
  },
  { key: 'contract_end', label: 'Vencimiento', defaultVisible: false,
    render: (s) => <td className="px-2 py-2 text-white/60 text-[11px]" key="ce">{s.site?.contract_end?.slice(0, 10) || '—'}</td>,
  },
];

const TotemDashboard = () => {
  const [data, setData] = useState(null);
  const [problematic, setProblematic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(24);
  const [minCount, setMinCount] = useState(3);
  const [probCols, setProbCols] = useVisibleColumns(PROBLEMATIC_COLUMNS, 'totem.problematic.cols.v1');
  const activeProbCols = useMemo(() => PROBLEMATIC_COLUMNS.filter((c) => probCols.has(c.key)), [probCols]);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [d, p] = await Promise.all([
        api.get('/incidents/dashboard-totem'),
        api.get(`/incidents/problematic-sites?min_count=${minCount}&months=${months}`),
      ]);
      setData(d.data);
      setProblematic(p.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [months, minCount]);

  if (loading || !data) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-blue-400" size={32}/></div>;
  if (data.total === 0) {
    return (
      <div className="p-8 text-center max-w-lg mx-auto">
        <AlertTriangle className="mx-auto text-white/30 mb-3" size={40}/>
        <h2 className="text-2xl font-black mb-2">Sin datos TOTEM</h2>
        <p className="text-white/50 text-sm">Todavía no se han importado incidencias TOTEM al sistema.</p>
      </div>
    );
  }

  const typeData = data.by_type.map((t) => ({ name: t.label, count: t.count, cost: t.cost, key: t.type }));
  const monthly = data.monthly.map((m) => ({ month: m.month, count: m.count, cost: m.cost }));
  const topSites = data.top_sites.slice(0, 15);

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="totem-dashboard-page">
      <div className="mb-6">
        <div className="text-xs font-bold tracking-widest text-blue-300">HISTÓRICO TOTEM · MANTENIMIENTO CORRECTIVO</div>
        <h1 className="text-3xl sm:text-4xl font-black mt-1">Dashboard incidencias TOTEM</h1>
        <p className="text-white/50 text-sm mt-1">{fmt(data.total)} incidencias importadas · rango histórico completo · datos actualizados en cada re-import.</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Kpi icon={Activity} label="Total incidencias" value={fmt(data.total)} sub={`${data.open_now} abiertas ahora`}/>
        <Kpi icon={Euro} label="Coste TOTEM acumulado" value={eur(data.total_cost)} sub="Importe responsabilidad TOTEM" tone="red"/>
        <Kpi icon={AlertTriangle} label="Prioridad ALTA" value={fmt(data.by_priority.alta || 0)} sub={`Media ${fmt(data.by_priority.media || 0)} · Baja ${fmt(data.by_priority.baja || 0)}`} tone="orange"/>
        <Kpi icon={Flame} label="Sites problemáticos" value={fmt(problematic?.count || 0)} sub={`≥ ${minCount} inc. últimos ${months} meses`} tone="red"/>
      </div>

      {/* Gráficas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Por tipo — pie */}
        <div className="bg-black/30 border border-white/10 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3 text-white/80 font-bold text-sm"><Activity size={14}/> Distribución por tipo</div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={typeData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => `${e.name}: ${e.count}`}>
                {typeData.map((t) => (
                  <Cell key={t.key} fill={TYPE_COLORS[t.key] || '#94a3b8'}/>
                ))}
              </Pie>
              <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: 8 }}
                       formatter={(v, n, p) => [`${fmt(v)} inc · ${eur(p.payload.cost)}`, p.payload.name]}/>
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Coste por tipo — bar */}
        <div className="bg-black/30 border border-white/10 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3 text-white/80 font-bold text-sm"><Euro size={14}/> Coste TOTEM por tipo</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={typeData} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222"/>
              <XAxis type="number" tickFormatter={(v) => v > 1e6 ? `${(v/1e6).toFixed(1)}M` : `${(v/1e3).toFixed(0)}k`} tick={{ fill: '#888', fontSize: 11 }}/>
              <YAxis type="category" dataKey="name" width={140} tick={{ fill: '#ccc', fontSize: 11 }}/>
              <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: 8 }} formatter={(v) => eur(v)}/>
              <Bar dataKey="cost" fill="#60a5fa" radius={[0, 4, 4, 0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Evolución mensual */}
      <div className="bg-black/30 border border-white/10 rounded-2xl p-4 mb-6">
        <div className="flex items-center gap-2 mb-3 text-white/80 font-bold text-sm"><TrendingUp size={14}/> Evolución mensual (incidencias y coste TOTEM)</div>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={monthly} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#222"/>
            <XAxis dataKey="month" tick={{ fill: '#888', fontSize: 11 }}/>
            <YAxis yAxisId="l" tick={{ fill: '#888', fontSize: 11 }}/>
            <YAxis yAxisId="r" orientation="right" tick={{ fill: '#888', fontSize: 11 }} tickFormatter={(v) => v > 1e6 ? `${(v/1e6).toFixed(1)}M€` : `${(v/1e3).toFixed(0)}k€`}/>
            <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: 8 }}
                     formatter={(v, n) => n === 'count' ? [fmt(v), 'Incidencias'] : [eur(v), 'Coste']}/>
            <Legend wrapperStyle={{ fontSize: 12 }}/>
            <Line yAxisId="l" type="monotone" dataKey="count" name="Incidencias" stroke="#60a5fa" strokeWidth={2} dot={false}/>
            <Line yAxisId="r" type="monotone" dataKey="cost" name="Coste (€)" stroke="#f87171" strokeWidth={2} dot={false}/>
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Top sites problemáticos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-black/30 border border-white/10 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3 text-white/80 font-bold text-sm"><Building2 size={14}/> Top 15 sites con más incidencias</div>
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={topSites} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222"/>
              <XAxis type="number" tick={{ fill: '#888', fontSize: 11 }}/>
              <YAxis type="category" dataKey="site_code" width={100} tick={{ fill: '#ccc', fontSize: 11 }}/>
              <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: 8 }}
                       formatter={(v, n, p) => [`${fmt(v)} inc · ${eur(p.payload.cost)}`, 'TOTEM']}/>
              <Bar dataKey="count" fill="#f87171" radius={[0, 4, 4, 0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-black/30 border border-white/10 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3 text-white/80 font-bold text-sm"><MapPin size={14}/> Top provincias</div>
          {data.by_province.length === 0 || (data.by_province.length === 1 && data.by_province[0].province === 'Sin provincia') ? (
            <div className="text-white/40 text-sm py-8 text-center">
              La mayoría de sites Totem no están linkeados al portfolio todavía.<br/>
              Sube los portfolios Z1-Z3 para ver el ranking por provincia.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={360}>
              <BarChart data={data.by_province.slice(0, 15)} layout="vertical" margin={{ left: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#222"/>
                <XAxis type="number" tick={{ fill: '#888', fontSize: 11 }}/>
                <YAxis type="category" dataKey="province" width={110} tick={{ fill: '#ccc', fontSize: 11 }}/>
                <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: 8 }}
                         formatter={(v, n, p) => [`${fmt(v)} inc · ${eur(p.payload.cost)}`, p.payload.province]}/>
                <Bar dataKey="count" fill="#34d399" radius={[0, 4, 4, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Sites problemáticos alertados */}
      <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-4">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-2 text-red-300 font-bold text-sm">
            <Flame size={16}/> Sites problemáticos alertados ({problematic?.count || 0})
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-white/50">≥</span>
            <select value={minCount} onChange={(e) => setMinCount(Number(e.target.value))} data-testid="problematic-min-count"
              className="bg-black/40 border border-white/10 rounded px-2 py-1 text-white">
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={5}>5</option>
              <option value={10}>10</option>
            </select>
            <span className="text-white/50">inc. en</span>
            <select value={months} onChange={(e) => setMonths(Number(e.target.value))} data-testid="problematic-months"
              className="bg-black/40 border border-white/10 rounded px-2 py-1 text-white">
              <option value={6}>6 meses</option>
              <option value={12}>12 meses</option>
              <option value={24}>24 meses</option>
              <option value={60}>5 años</option>
            </select>
            <ColumnPicker columns={PROBLEMATIC_COLUMNS} value={probCols} onChange={setProbCols} storageKey="totem.problematic.cols.v1"/>
          </div>
        </div>
        {problematic?.items?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-white/50 uppercase text-[10px]">
                <tr>
                  {activeProbCols.map((c) => (
                    <th key={c.key} className={`px-2 py-2 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.label}</th>
                  ))}
                  <th/>
                </tr>
              </thead>
              <tbody>
                {groupByProvince(problematic.items.map((s) => ({ ...s, code: s.site_code, alt_code: s.site_code, province: s.site?.province }))).map((group) => (
                  <React.Fragment key={group.province}>
                    <tr className="bg-white/[0.04] border-t border-white/10" data-testid={`totem-problematic-province-${group.province}`}>
                      <td colSpan={activeProbCols.length + 1} className="px-2 py-1.5">
                        <div className="flex items-center gap-2 text-[11px] font-black tracking-widest text-red-300">
                          <MapPin size={12}/>
                          <span className="uppercase">{group.province}</span>
                          <span className="text-white/40 font-mono">· {group.items.length}</span>
                        </div>
                      </td>
                    </tr>
                    {group.items.map((s, i) => (
                      <tr key={`${group.province}-${s.site_code}-${i}`} className="border-t border-white/5 hover:bg-white/5">
                        {activeProbCols.map((c) => c.render(s, { eur, TYPE_COLORS }))}
                        <td className="px-2 py-2 text-right">
                          <button onClick={() => navigate(`/app/incidencias?q=${encodeURIComponent(s.site_code)}`)}
                            className="text-blue-300 hover:text-blue-200 text-[11px] font-bold underline"
                            data-testid={`goto-inc-${s.site_code}`}>
                            Ver incidencias →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-white/50 text-sm py-6 text-center">Ningún site supera el umbral configurado.</div>
        )}
      </div>
    </div>
  );
};

export default TotemDashboard;
